/**
 * StackTrace
 *
 * Error output display for AI Elements execution surfaces.
 *
 * Primary responsibilities:
 * - Render stderr and stack traces in a readable panel
 * - Keep error styling consistent with sandbox output
 *
 * Dependencies:
 * - lib/utils
 */

'use client'

import type { ComponentProps } from 'react'
import { AlertCircle } from 'lucide-react'
import { cn } from '@/lib/utils'

export interface StackTraceProps extends ComponentProps<'div'> {
  trace?: string
}

export function StackTrace({ trace, className, children, ...props }: StackTraceProps) {
  const content = trace ?? (typeof children === 'string' ? children : '')

  return (
    <div
      className={cn(
        'rounded-md border border-destructive/40 bg-destructive/10 text-destructive',
        className
      )}
      {...props}
    >
      <div className="flex items-center gap-2 border-b border-destructive/20 px-2 py-1.5 text-xs font-medium">
        <AlertCircle className="size-3.5" />
        <span>Error output</span>
      </div>
      <pre className="max-h-80 overflow-auto whitespace-pre-wrap break-words p-3 font-mono text-xs">
        {content || children}
      </pre>
    </div>
  )
}

export function StackTraceContent(props: ComponentProps<'pre'>) {
  return <pre {...props} />
}
